import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, query, getDocs, doc, setDoc, onSnapshot, getDoc, deleteDoc, addDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import PasswordInput from '../components/common/PasswordInput';
import useConfirmationModal from '../components/common/useConfirmationModal';
import { db, auth } from '../App';
import { appId, firebaseConfig } from '../config/firebaseConfig';
import { ADMIN_EMAIL, PERMISSIONS_CONFIG } from '../config/permissionsConfig';
import { ACCESS_PAGES } from '../config/menuConfig';

export default function ManageAccessPage({ userId }) {
    const [activeTab, setActiveTab] = useState('users'); // 'users' ou 'groups'
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [allClients, setAllClients] = useState([]);
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [myPermission, setMyPermission] = useState('none');

    // Estados de edição
    const [userPermissions, setUserPermissions] = useState({});
    const [userGroupIds, setUserGroupIds] = useState([]);
    const [userPassword, setUserPassword] = useState('');
    const [userPhone, setUserPhone] = useState('');
    
    const [groupName, setGroupName] = useState('');
    const [groupPermissions, setGroupPermissions] = useState({});
    const [companyTab, setCompanyTab] = useState('contrato');
    const [groupFilter, setGroupFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [bulkSelection, setBulkSelection] = useState(new Set()); // Novo: seleção em massa
    const [isBulkMode, setIsBulkMode] = useState(false);
    
    // Estados de override do usuário
    const [userAllowedCompanies, setUserAllowedCompanies] = useState([]);
    const [userUiItems, setUserUiItems] = useState({});
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    
    // Itens de tela granulares
    const [expandedPageId, setExpandedPageId] = useState(null); 
    const [groupUiItems, setGroupUiItems] = useState({}); 
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();

    const TECHNICAL_PAGES = ACCESS_PAGES; // fonte única: src/config/menuConfig.js (já agrupada por seção)

    const paths = {
        users: `/artifacts/${appId}/public/data/user_permissions`,
        groups: `/artifacts/${appId}/public/data/groups`,
        clients: `/artifacts/intranet-arcus-prod/public/data/clients`,
        clientsAvulsos: `/artifacts/intranet-arcus-prod/public/data/clients-avulsos`,
        clientsTickets: `/artifacts/${appId}/public/data/clientes_chamados`
    };

    useEffect(() => {
        const checkMyAccess = async () => {
            if (auth.currentUser.email === ADMIN_EMAIL) {
                setMyPermission('edit');
                return;
            }
            const myDoc = await getDoc(doc(db, paths.users, auth.currentUser.uid));
            if (myDoc.exists()) {
                setMyPermission(myDoc.data().permissions?.['manage-access'] || 'none');
            }
        };
        checkMyAccess();
    }, []);

    useEffect(() => {
        const unsubUsers = onSnapshot(collection(db, paths.users), (snap) => {
            setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
            setLoading(false);
        });
        const unsubGroups = onSnapshot(collection(db, paths.groups), (snap) => {
            setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const fetchClients = async () => {
            const [c1, c2] = await Promise.all([
                getDocs(collection(db, paths.clients)),
                getDocs(collection(db, paths.clientsAvulsos))
            ]);
            
            const combined = [
                ...c1.docs.map(d => ({ id: d.id, name: d.data().name, type: 'Contrato' })),
                ...c2.docs.map(d => ({ id: d.id, name: d.data().name, type: 'Avulso' }))
            ];
            
            setAllClients(combined.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        };
        fetchClients();

        return () => { unsubUsers(); unsubGroups(); };
    }, []);
    
    const userCountsByGroup = useMemo(() => {
        const counts = {};
        users.forEach(u => {
            const ids = u.groupIds || (u.groupId ? [u.groupId] : []);
            ids.forEach(id => {
                counts[id] = (counts[id] || 0) + 1;
            });
        });
        return counts;
    }, [users]);

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setUserPermissions(user.permissions || {});
        const initialGroups = user.groupIds || (user.groupId ? [user.groupId] : []);
        setUserGroupIds(initialGroups);
        setUserPhone(user.phone || '');
        setUserAllowedCompanies(user.allowedCompanies || []);
        setUserUiItems(user.uiItems || {});
        setUserPassword('');
        setMessage('');
    };

    const handleSaveUser = async () => {
        if (!selectedUser) return;

        const executeBulkSave = async () => {
            setIsSaving(true);
            try {
                const batch = writeBatch(db);
                Array.from(bulkSelection).forEach(uid => {
                    const userRef = doc(db, paths.users, uid);
                    batch.update(userRef, {
                        groupIds: userGroupIds,
                        groupId: userGroupIds[0] || '',
                        permissions: userPermissions,
                        uiItems: userUiItems,
                        allowedCompanies: userAllowedCompanies,
                        updatedAt: serverTimestamp()
                    });
                });
                await batch.commit();
                setMessage("✓ Permissões aplicadas em massa com sucesso!");
                setTimeout(() => setMessage(''), 3000);
            } catch (err) {
                setMessage("Erro ao salvar em massa.");
            } finally {
                setIsSaving(false);
            }
        };

        if (isBulkMode) {
            showConfirmation({
                title: "Ação em Massa",
                message: `Deseja aplicar estas permissões a ${bulkSelection.size} utilizadores?`,
                onConfirm: executeBulkSave,
                confirmColor: "bg-purple-600",
                confirmText: "Aplicar Agora"
            });
            return;
        }

        setIsSaving(true);
        try {
            let uid = selectedUser.uid;
            if (!userPhone || userPhone.trim().length < 8) {
                setMessage("Erro: O Telefone (WhatsApp) é obrigatório!");
                setTimeout(() => setMessage(''), 4000);
                setIsSaving(false);
                return;
            }
            if (selectedUser.isNew) {
                if (!selectedUser.email || !userPassword) {
                    setMessage("Erro: Email e Senha são obrigatórios!");
                    setTimeout(() => setMessage(''), 4000);
                    setIsSaving(false);
                    return;
                }
                const secondaryApp = getApps().find(app => app.name === 'Secondary') || initializeApp(firebaseConfig, 'Secondary');
                const secondaryAuth = getAuth(secondaryApp);
                try {
                    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, selectedUser.email, userPassword);
                    uid = userCredential.user.uid;
                    await signOut(secondaryAuth);
                } catch (authError) {
                    console.error("Erro ao criar Auth:", authError);
                    let msg = "Erro ao criar conta no Firebase.";
                    if (authError.code === 'auth/email-already-in-use') msg = "Este email já está em uso!";
                    if (authError.code === 'auth/weak-password') msg = "Senha muito fraca (mínimo 6 caracteres)!";
                    setMessage(msg);
                    setTimeout(() => setMessage(''), 4000);
                    setIsSaving(false);
                    return;
                }
            }
            await setDoc(doc(db, paths.users, uid), {
                email: selectedUser.email,
                permissions: userPermissions,
                groupIds: userGroupIds,
                groupId: userGroupIds[0] || '', 
                phone: userPhone,
                allowedCompanies: userAllowedCompanies,
                uiItems: userUiItems,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            setMessage(selectedUser.isNew ? 'Usuário criado e acesso configurado!' : 'Usuário atualizado com sucesso!');
            setUserPassword('');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Erro ao salvar usuário.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        
        showConfirmation({
            title: "Excluir Usuário",
            message: `Tem certeza que deseja excluir permanentemente o registro de acesso de ${selectedUser.email}?`,
            onConfirm: async () => {
                try {
                    setIsSaving(true);
                    await deleteDoc(doc(db, paths.users, selectedUser.uid));
                    setMessage('Usuário removido da listagem.');
                    setSelectedUser(null);
                    setTimeout(() => setMessage(''), 3000);
                } catch (error) {
                    setMessage('Erro ao excluir usuário.');
                } finally {
                    setIsSaving(false);
                }
            },
            confirmColor: "bg-red-500",
            confirmText: "Excluir"
        });
    };

    const handleGroupSelect = (group) => {
        if (!group) {
            setSelectedGroup({ id: '', name: '', isNew: true });
            setGroupName('');
            setGroupPermissions({});
            setGroupUiItems({});
        } else {
            setSelectedGroup(group);
            setGroupName(group.name || '');
            setGroupPermissions(group.permissions || {});
            setGroupUiItems(group.uiItems || {});
        }
        setMessage('');
    };

    const handleSaveGroup = async () => {
        if (!groupName.trim()) return;
        setIsSaving(true);
        try {
            const groupData = {
                name: groupName,
                permissions: groupPermissions,
                uiItems: groupUiItems,
                updatedAt: new Date().toISOString()
            };
            if (selectedGroup.isNew) {
                const docRef = await addDoc(collection(db, paths.groups), groupData);
                setSelectedGroup({ id: docRef.id, ...groupData });
            } else {
                await setDoc(doc(db, paths.groups, selectedGroup.id), groupData);
                setSelectedGroup({ id: selectedGroup.id, ...groupData });
            }
            setMessage('✓ Alterações salvas com sucesso!');
            setTimeout(() => setMessage(''), 4000);
        } catch (error) {
            setMessage('Erro ao salvar grupo.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteGroup = async (id) => {
        showConfirmation({
            title: "Excluir Grupo",
            message: 'Tem certeza que deseja excluir este grupo permanentemente?',
            onConfirm: async () => {
                await deleteDoc(doc(db, paths.groups, id));
                setSelectedGroup(null);
                setMessage('Grupo excluído com sucesso.');
                setTimeout(() => setMessage(''), 3000);
            },
            confirmColor: "bg-red-500",
            confirmText: "Excluir"
        });
    };

    if (loading) return <div className="p-8 text-cyan-400 font-bold animate-pulse">Sincronizando acessos...</div>;

    if (auth.currentUser.email !== ADMIN_EMAIL && myPermission !== 'edit') {
        return <div className="p-8 text-red-500 font-bold text-center">ACESSO NEGADO</div>;
    }

    return (
        <div className="pt-8 md:pt-12 px-4 md:px-8 pb-8 font-sans text-white bg-slate-900 min-h-screen">
            <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-cyan-400">Gerenciar Acessos</h1>
                    <p className="text-slate-400 mt-1">Configuração de Grupos e Permissões de Usuários.</p>
                </div>
                <div className="flex bg-slate-800 p-1 rounded-xl border border-slate-700">
                    <button 
                        onClick={() => { setActiveTab('users'); setSelectedUser(null); setSelectedGroup(null); }}
                        className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${activeTab === 'users' ? 'bg-cyan-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                    >
                        Utilizadores
                    </button>
                    <button 
                        onClick={() => { setActiveTab('groups'); setSelectedUser(null); setSelectedGroup(null); }}
                        className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${activeTab === 'groups' ? 'bg-cyan-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                    >
                        Grupos
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* BARRA LATERAL */}
                <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden h-[calc(100vh-250px)] flex flex-col">
                    <div className="p-4 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center">
                        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">{activeTab === 'users' ? 'Utilizadores' : 'Grupos'}</h2>
                        <button 
                            onClick={() => activeTab === 'users' ? setSelectedUser({ uid: '', email: '', isNew: true }) : handleGroupSelect(null)} 
                            className="text-[10px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 px-2 py-1 rounded hover:bg-cyan-500 hover:text-white transition-all font-bold"
                        >
                            + NOVO
                        </button>
                    </div>

                    <div className="p-3 bg-slate-900/30 border-b border-slate-700 space-y-2">
                        <div className="relative">
                            <input 
                                type="text"
                                placeholder={`Pesquisar ${activeTab === 'users' ? 'utilizador' : 'grupo'}...`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full bg-slate-800 border border-slate-700 rounded-lg py-1.5 pl-8 pr-3 text-xs text-slate-300 outline-none focus:ring-1 focus:ring-cyan-500"
                            />
                            <svg className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-700 custom-scrollbar">
                        {activeTab === 'users' ? (
                            users
                            .filter(u => u.email.toLowerCase().includes(searchTerm.toLowerCase()))
                            .sort((a, b) => a.email.localeCompare(b.email))
                            .map(u => {
                                const isSelected = selectedUser?.uid === u.uid;
                                const inBulk = bulkSelection.has(u.uid);
                                
                                return (
                                    <div 
                                        key={u.uid} 
                                        className={`flex items-center group transition-all border-l-4 ${isSelected ? 'bg-cyan-500/10 border-cyan-500' : inBulk ? 'bg-slate-700/50 border-purple-500/50' : 'hover:bg-slate-800 border-transparent'}`}
                                    >
                                        <div className="pl-4 py-4">
                                            <input 
                                                type="checkbox"
                                                checked={inBulk}
                                                onChange={(e) => {
                                                    const newSelection = new Set(bulkSelection);
                                                    if (e.target.checked) newSelection.add(u.uid);
                                                    else newSelection.delete(u.uid);
                                                    setBulkSelection(newSelection);
                                                    
                                                    if (newSelection.size > 1) {
                                                        setIsBulkMode(true);
                                                        setSelectedUser({ uid: 'bulk', email: `${newSelection.size} utilizadores selecionados` });
                                                        // Reset dos estados para configuração limpa no modo massa
                                                        setUserGroupIds([]);
                                                        setUserPermissions({});
                                                        setUserUiItems({});
                                                        setUserAllowedCompanies([]);
                                                        setUserPhone('');
                                                    } else if (newSelection.size === 1) {
                                                        const singleUid = Array.from(newSelection)[0];
                                                        const singleUser = users.find(usr => usr.uid === singleUid);
                                                        handleUserSelect(singleUser);
                                                        setIsBulkMode(false);
                                                    } else {
                                                        setIsBulkMode(false);
                                                        setSelectedUser(null);
                                                    }
                                                }}
                                                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-500 cursor-pointer"
                                            />
                                        </div>
                                        
                                        <button 
                                            onClick={() => {
                                                handleUserSelect(u);
                                                setBulkSelection(new Set([u.uid]));
                                                setIsBulkMode(false);
                                            }}
                                            className="flex-1 text-left p-4 pr-3 min-w-0"
                                        >
                                            <span className={`font-bold truncate block ${isSelected ? 'text-cyan-400' : 'text-slate-200'}`}>{u.email}</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[9px] text-slate-500 uppercase font-mono">{u.uid.substring(0, 8)}...</span>
                                                {(() => {
                                                    const ids = u.groupIds || (u.groupId ? [u.groupId] : []);
                                                    const activeGroups = ids.map(id => groups.find(g => g.id === id)?.name).filter(Boolean);
                                                    
                                                    if (activeGroups.length > 0) {
                                                        return (
                                                            <span className="text-[9px] bg-purple-500/20 text-purple-400 px-1.5 rounded border border-purple-500/30 font-bold uppercase truncate max-w-[120px]">
                                                                {activeGroups.join(', ')}
                                                            </span>
                                                        );
                                                    }
                                                    return (
                                                        <span className="text-[9px] bg-slate-500/10 text-slate-500 px-1.5 rounded border border-slate-500/20 font-bold uppercase">Individual</span>
                                                    );
                                                })()}
                                            </div>
                                        </button>
                                    </div>
                                );
                            })
                        ) : (
                            groups
                            .filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(g => (
                                <button 
                                    key={g.id} 
                                    onClick={() => handleGroupSelect(g)}
                                    className={`w-full text-left p-4 hover:bg-slate-700 transition-all flex justify-between items-center ${selectedGroup?.id === g.id ? 'bg-cyan-500/10 border-l-4 border-cyan-500' : ''}`}
                                >
                                    <div className="flex flex-col gap-1">
                                        <span className="font-bold text-slate-200">{g.name}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-bold text-cyan-500/80 uppercase tracking-tighter">
                                                {userCountsByGroup[g.id] || 0} Usuários
                                            </span>
                                        </div>
                                    </div>
                                    <svg className="h-4 w-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* EDITOR CENTRAL */}
                <div className="md:col-span-2 bg-slate-800 rounded-2xl border border-slate-700 flex flex-col h-[calc(100vh-250px)] overflow-hidden shadow-2xl relative">
                    {message && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 animate-bounce">
                            <div className={`px-6 py-2 rounded-full font-black text-[10px] uppercase tracking-widest shadow-2xl border ${message.includes('Erro') ? 'bg-red-500 border-red-400 text-white' : 'bg-emerald-500 border-emerald-400 text-white'}`}>
                                {message}
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && selectedUser && (
                        <div className="flex flex-col h-full animate-fadeIn">
                            {/* HEADER DO EDITOR DE USUÁRIOS */}
                            <>
                            <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-900/20 shrink-0">
                                {selectedUser.isNew ? (
                                    <div className="flex flex-col sm:flex-row gap-3 w-full mr-4">
                                        <div className="flex-1">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">Email do Usuário</label>
                                            <input 
                                                type="email" 
                                                value={selectedUser.email} 
                                                onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })}
                                                placeholder="exemplo@arcusinformatica.com.br"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm font-bold text-white focus:outline-none focus:border-cyan-500"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">Senha Inicial</label>
                                            <PasswordInput 
                                                id="userPassword" 
                                                name="userPassword" 
                                                value={userPassword} 
                                                onChange={e => setUserPassword(e.target.value)}
                                                placeholder="Mínimo 6 caracteres"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm font-bold text-white focus:outline-none focus:border-cyan-500"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 block">Telefone</label>
                                            <input 
                                                type="text" 
                                                value={userPhone} 
                                                onChange={e => setUserPhone(e.target.value)}
                                                placeholder="(00) 00000-0000"
                                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm font-bold text-white focus:outline-none focus:border-cyan-500"
                                            />
                                        </div>
                                    </div>
                                ) : isBulkMode ? (
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <span className="bg-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-lg animate-pulse">MODO MASSA ATIVO</span>
                                            <h3 className="text-xl font-bold text-white">{bulkSelection.size} Utilizadores Selecionados</h3>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-1 italic">As alterações abaixo serão aplicadas a todos os perfis marcados.</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col">
                                        <h3 className="text-xl font-bold text-white truncate max-w-md">{selectedUser.email}</h3>
                                        <div className="flex items-center gap-4 mt-2">
                                            <div className="flex flex-col">
                                                <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-0.5 block">Telefone</label>
                                                <input 
                                                    type="text" 
                                                    value={userPhone} 
                                                    onChange={e => setUserPhone(e.target.value)}
                                                    placeholder="(00) 00000-0000"
                                                    className="bg-transparent border-none p-0 text-xs font-bold text-cyan-400 focus:outline-none focus:ring-0"
                                                />
                                            </div>
                                            <div className="h-4 w-px bg-slate-700 mx-1"></div>
                                            <p className="text-xs text-slate-500">Ajuste fino de permissões.</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-2 shrink-0">
                                    <button 
                                        onClick={() => setShowCopyModal(true)}
                                        className="bg-purple-500/10 hover:bg-purple-500 text-purple-400 hover:text-white px-4 py-2 rounded-xl border border-purple-500/30 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                                        title="Copiar permissões de outro usuário"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                                        Copiar Perfil
                                    </button>
                                    <button 
                                        onClick={() => setShowCompanyModal(true)}
                                        className="bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white px-4 py-2 rounded-xl border border-slate-600 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        Vínculo de Empresas
                                        {userAllowedCompanies.length > 0 && <span className="bg-cyan-500 text-white w-4 h-4 rounded-full flex items-center justify-center text-[8px]">{userAllowedCompanies.length}</span>}
                                    </button>
                                    <button onClick={handleDeleteUser} disabled={isSaving} className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white p-2 rounded-xl border border-red-500/30 transition-all">
                                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                    <button onClick={handleSaveUser} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50">
                                        {isSaving ? 'Salvando...' : 'Salvar'}
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">


                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start pb-10">
                                    {/* COLUNA ESQUERDA: GRUPOS */}
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Associação de Grupos</h4>
                                        </div>
                                        <div className="bg-slate-900/40 rounded-2xl border border-slate-700/50 overflow-hidden">
                                            <div className="divide-y divide-slate-700/50 max-h-[500px] overflow-y-auto custom-scrollbar">
                                                {groups.sort((a, b) => a.name.localeCompare(b.name)).map(g => {
                                                    const isSelected = userGroupIds.includes(g.id);
                                                    return (
                                                        <button 
                                                            key={g.id}
                                                            type="button"
                                                            onClick={() => {
                                                                if (isSelected) setUserGroupIds(userGroupIds.filter(id => id !== g.id));
                                                                else setUserGroupIds([...userGroupIds, g.id]);
                                                            }}
                                                            className={`w-full flex items-center gap-4 p-4 transition-all hover:bg-slate-800/50 ${isSelected ? 'bg-purple-500/5' : ''}`}
                                                        >
                                                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-purple-500 border-purple-500 shadow-lg shadow-purple-500/20' : 'border-slate-600 bg-slate-900'}`}>
                                                                {isSelected && <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                            </div>
                                                            <div className="flex flex-col text-left">
                                                                <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-slate-400'}`}>{g.name}</span>
                                                                <span className="text-[9px] text-slate-600 uppercase font-mono tracking-tighter">{g.id.substring(0, 8)}</span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="p-3 bg-slate-950/20 border-t border-slate-700/50">
                                                <p className="text-[9px] text-slate-500 italic text-center">Permissões cumulativas.</p>
                                            </div>
                                        </div>
                                    </section>

                                    {/* COLUNA DIREITA: OVERRIDES */}
                                    <section>
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500"></div>
                                                Permissões e Overrides
                                            </h4>
                                        </div>

                                        {(() => {
                                            const selectedGroupsData = groups.filter(g => userGroupIds.includes(g.id));
                                            const mergedGroupPermissions = {};
                                            selectedGroupsData.forEach(group => {
                                                if (group.permissions) {
                                                    Object.keys(group.permissions).forEach(pageId => {
                                                        const current = mergedGroupPermissions[pageId] || 'none';
                                                        const groupLvl = group.permissions[pageId];
                                                        if (groupLvl === 'edit' || (groupLvl === 'read' && current === 'none')) {
                                                            mergedGroupPermissions[pageId] = groupLvl;
                                                        }
                                                    });
                                                }
                                            });

                                            const filteredPages = TECHNICAL_PAGES.filter(page => mergedGroupPermissions[page.id] && mergedGroupPermissions[page.id] !== 'none');

                                            if (filteredPages.length === 0) {
                                                return (
                                                    <div className="flex flex-col items-center justify-center py-12 bg-slate-900/20 rounded-2xl border border-slate-800 border-dashed opacity-40">
                                                        <div className="text-2xl mb-2">📥</div>
                                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">Associe um grupo para<br/>configurar permissões.</p>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="space-y-3">
                                                    {filteredPages.map((page, pageIdx) => {
                                                        const showSectionHeader = pageIdx === 0 || filteredPages[pageIdx - 1].section !== page.section;
                                                        const groupLvl = mergedGroupPermissions[page.id] || 'none';
                                                        const userLvl = userPermissions[page.id]; 
                                                        const isOverridden = userLvl !== undefined && userLvl !== groupLvl;
                                                        const pageConfig = PERMISSIONS_CONFIG[page.id];
                                                        const hasUiItems = pageConfig?.uiItems;
                                                        const isExpanded = expandedPageId === `user-${page.id}`;

                                                        return (
                                                            <React.Fragment key={page.id}>
                                                            {showSectionHeader && <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest pt-2">{page.section}</p>}
                                                            <div className={`bg-slate-900/40 rounded-xl border transition-all ${isOverridden ? 'border-cyan-500 bg-cyan-500/5' : 'border-slate-700/50'} ${isExpanded ? 'ring-1 ring-cyan-500/30' : ''}`}>
                                                                <div 
                                                                    onClick={() => setExpandedPageId(isExpanded ? null : `user-${page.id}`)}
                                                                    className="p-3 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-800/30 transition-all"
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <svg className={`h-3 w-3 text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" /></svg>
                                                                        <div className="flex flex-col">
                                                                            <span className={`font-bold text-[10px] uppercase tracking-wider ${isExpanded ? 'text-cyan-400' : 'text-slate-200'}`}>{page.label}</span>
                                                                            {isOverridden && <span className="text-[7px] font-black text-cyan-500 uppercase tracking-tighter mt-0.5">Override Ativo</span>}
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                                                        {['read', 'edit'].map(lvl => {
                                                                            const isActive = userLvl === lvl || (userLvl === undefined && groupLvl === lvl);
                                                                            return (
                                                                                <button 
                                                                                    key={lvl}
                                                                                    onClick={() => setUserPermissions(p => ({ ...p, [page.id]: lvl }))}
                                                                                    className={`px-3 py-1.5 rounded-[4px] text-[7px] font-black uppercase tracking-widest transition-all ${isActive ? (lvl === 'read' ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white') : 'bg-slate-800 text-slate-600 hover:text-slate-400'}`}
                                                                                >
                                                                                    {lvl === 'read' ? 'Ver' : 'Edit'}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                                {isExpanded && ((userLvl === 'edit') || (userLvl === undefined && groupLvl === 'edit')) && hasUiItems && (
                                                                    <div className="px-3 pb-3 pt-1 border-t border-slate-700/30 bg-slate-900/20">
                                                                        <div className="grid grid-cols-1 gap-1.5 mt-2">
                                                                            {pageConfig.uiItems.map(item => {
                                                                                const isChecked = userUiItems[page.id]?.[item.id] !== false;
                                                                                return (
                                                                                    <label key={item.id} className={`flex items-center justify-between p-2 px-3 rounded-lg border transition-all cursor-pointer ${isChecked ? 'bg-cyan-500/5 border-cyan-500/20 text-white' : 'bg-slate-900/20 border-slate-800 text-slate-600'}`}>
                                                                                        <span className="text-[9px] font-bold uppercase tracking-tight">{item.label}</span>
                                                                                        <input 
                                                                                            type="checkbox" 
                                                                                            checked={isChecked}
                                                                                            onChange={() => {
                                                                                                const currentItems = userUiItems[page.id] || {};
                                                                                                setUserUiItems({
                                                                                                    ...userUiItems,
                                                                                                    [page.id]: { ...currentItems, [item.id]: !isChecked }
                                                                                                });
                                                                                            }}
                                                                                            className="w-3.5 h-3.5 rounded border-slate-700 text-cyan-500 bg-slate-800"
                                                                                        />
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </section>
                                </div>
                            </div>
                        </>
                    </div>
                )}

                    {activeTab === 'groups' && selectedGroup && (
                        <div className="flex flex-col h-full animate-fadeIn">
                            <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-900/20 shrink-0">
                                <input 
                                    type="text" 
                                    value={groupName} 
                                    onChange={e => setGroupName(e.target.value)} 
                                    placeholder="Nome do Grupo..."
                                    className="text-xl font-bold bg-transparent border-b border-cyan-500/50 text-white focus:outline-none focus:border-cyan-400 w-full mr-4"
                                />
                                <div className="flex gap-2 shrink-0">
                                    {selectedGroup.id && (
                                        <button onClick={() => handleDeleteGroup(selectedGroup.id)} className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white p-2 rounded-xl border border-red-500/30 transition-all">
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    )}
                                    <button onClick={handleSaveGroup} disabled={isSaving} className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 px-8 rounded-xl shadow-lg transition-all disabled:opacity-50">
                                        {isSaving ? 'Salvando...' : 'Salvar Grupo'}
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 space-y-10 custom-scrollbar">
                                <section>
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-500"></div>
                                        Permissões de Tela do Grupo
                                    </h4>
                                    <div className="space-y-3">
                                        {TECHNICAL_PAGES.map((page, pageIdx) => {
                                            const currentLvl = groupPermissions[page.id] || 'none';
                                            const isReleased = currentLvl !== 'none';
                                            const showSectionHeader = pageIdx === 0 || TECHNICAL_PAGES[pageIdx - 1].section !== page.section;
                                            return (
                                                <React.Fragment key={page.id}>
                                                {showSectionHeader && <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest pt-2">{page.section}</p>}
                                                <div className={`bg-slate-900/40 rounded-xl border transition-all ${isReleased ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-700/30 opacity-60'}`}>
                                                    <div className="p-3 flex items-center justify-between gap-4">
                                                        <div className="flex items-center gap-3">
                                                            <button 
                                                                onClick={() => setGroupPermissions(p => ({ ...p, [page.id]: isReleased ? 'none' : 'edit' }))}
                                                                className={`w-10 h-5 rounded-full transition-all relative ${isReleased ? 'bg-cyan-500' : 'bg-slate-700'}`}
                                                            >
                                                                <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${isReleased ? 'left-6' : 'left-1'}`}></div>
                                                            </button>
                                                            <div className="flex flex-col">
                                                                <span className={`font-bold text-xs uppercase tracking-wider ${isReleased ? 'text-white' : 'text-slate-500'}`}>{page.label}</span>
                                                                {isReleased && <span className="text-[8px] font-black text-cyan-400 uppercase tracking-tighter">Acesso Liberado</span>}
                                                            </div>
                                                        </div>
                                                        {isReleased && (
                                                            <div className="flex items-center gap-3 animate-fadeIn">
                                                                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-500/20">Acesso Total Liberado</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}

                    {!selectedUser && !selectedGroup && (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-600 p-10 text-center">
                            <div className="w-20 h-20 bg-slate-700/30 rounded-full flex items-center justify-center mb-4">
                                <svg className="h-10 w-10 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                            </div>
                            <h3 className="text-lg font-bold">Nenhum item selecionado</h3>
                            <p className="text-sm max-w-xs mt-2">Selecione um utilizador ou grupo para configurar.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL DE SELEÇÃO DE EMPRESAS */}
            {showCompanyModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-3xl border border-slate-700 shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-cyan-500/10 rounded-xl flex items-center justify-center text-cyan-400 border border-cyan-500/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Vincular Empresas</h3>
                                    <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Defina quais clientes {selectedUser?.email} pode visualizar.</p>
                                </div>
                            </div>
                            <button onClick={() => setShowCompanyModal(false)} className="text-slate-500 hover:text-white p-2 transition-all">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="p-6 flex-1 overflow-hidden flex flex-col gap-6">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <input 
                                        type="text" 
                                        placeholder="Buscar empresa..."
                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-cyan-500 transition-all"
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <svg className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </div>
                                <div className="flex bg-slate-800 p-1 rounded-xl border border-slate-700 shrink-0">
                                    {['contrato', 'avulso'].map(tab => (
                                        <button key={tab} onClick={() => setCompanyTab(tab)} className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${companyTab === tab ? 'bg-cyan-500 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                                            {tab}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                    {allClients.filter(c => {
                                        const matchesType = companyTab === 'contrato' ? c.type === 'Contrato' : c.type === 'Avulso';
                                        const matchesSearch = (c.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
                                        return matchesType && matchesSearch;
                                    }).map(client => {
                                        const isSelected = userAllowedCompanies.includes(client.name);
                                        return (
                                            <button 
                                                key={client.id} 
                                                onClick={() => {
                                                    if (isSelected) setUserAllowedCompanies(userAllowedCompanies.filter(c => c !== client.name));
                                                    else setUserAllowedCompanies([...userAllowedCompanies, client.name]);
                                                }} 
                                                className={`group relative text-left p-4 rounded-2xl border transition-all flex flex-col gap-1 ${isSelected ? 'bg-cyan-500/10 border-cyan-500 text-white shadow-lg' : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:border-slate-500'}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className={`text-[11px] font-bold truncate pr-4 ${isSelected ? 'text-cyan-400' : 'text-slate-200'}`}>{client.name}</span>
                                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all ${isSelected ? 'bg-cyan-500 border-cyan-500' : 'border-slate-700 bg-slate-900 group-hover:border-slate-500'}`}>
                                                        {isSelected && <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                                    </div>
                                                </div>
                                                <span className="text-[9px] text-slate-600 uppercase font-mono tracking-tighter">ID: {client.id.substring(0, 8)}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-800 bg-slate-950/30 flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                    <span className="text-cyan-400">{userAllowedCompanies.length}</span> Empresas Selecionadas
                                </div>
                                <div className="flex gap-4">
                                    <button 
                                        onClick={() => {
                                            const filtered = allClients.filter(c => {
                                                const matchesType = companyTab === 'contrato' ? c.type === 'Contrato' : c.type === 'Avulso';
                                                const matchesSearch = (c.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
                                                return matchesType && matchesSearch;
                                            });
                                            const names = filtered.map(c => c.name);
                                            setUserAllowedCompanies(prev => Array.from(new Set([...prev, ...names])));
                                        }} 
                                        className="text-[10px] font-black text-cyan-400 hover:text-cyan-300 uppercase tracking-widest transition-all"
                                    >
                                        Marcar Tudo
                                    </button>
                                    <button 
                                        onClick={() => {
                                            const filtered = allClients.filter(c => {
                                                const matchesType = companyTab === 'contrato' ? c.type === 'Contrato' : c.type === 'Avulso';
                                                const matchesSearch = (c.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
                                                return matchesType && matchesSearch;
                                            });
                                            const namesToRemove = filtered.map(c => c.name);
                                            setUserAllowedCompanies(prev => prev.filter(name => !namesToRemove.includes(name)));
                                        }} 
                                        className="text-[10px] font-black text-slate-400 hover:text-slate-300 uppercase tracking-widest transition-all"
                                    >
                                        Desmarcar Tudo
                                    </button>
                                    {userAllowedCompanies.length > 0 && (
                                        <button onClick={() => setUserAllowedCompanies([])} className="text-[10px] font-black text-red-400 hover:text-red-300 uppercase tracking-widest transition-all border-l border-slate-800 pl-4">Limpar Seleção</button>
                                    )}
                                </div>
                            </div>
                            <button 
                                onClick={() => setShowCompanyModal(false)}
                                className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2.5 px-10 rounded-xl shadow-lg transition-all"
                            >
                                Concluído
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* MODAL DE CÓPIA DE PERMISSÕES */}
            {showCopyModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-fadeIn">
                    <div className="bg-slate-900 w-full max-w-2xl max-h-[80vh] rounded-3xl border border-slate-700 shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-400 border border-purple-500/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Copiar Perfil de Acesso</h3>
                                    <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Selecione de quem deseja copiar as permissões para {selectedUser?.email}.</p>
                                </div>
                            </div>
                            <button onClick={() => setShowCopyModal(false)} className="text-slate-500 hover:text-white p-2 transition-all">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="p-4 bg-slate-800/30 border-b border-slate-800">
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Buscar usuário por email..."
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-purple-500 transition-all"
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <svg className="absolute left-3.5 top-3 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto divide-y divide-slate-800 custom-scrollbar">
                            {users
                                .filter(u => u.uid !== selectedUser?.uid && u.email.toLowerCase().includes((searchTerm || '').toLowerCase()))
                                .sort((a, b) => a.email.localeCompare(b.email))
                                .map(u => (
                                    <button 
                                        key={u.uid} 
                                        onClick={() => {
                                            showConfirmation({
                                                title: "Copiar Perfil",
                                                message: `Deseja substituir TODAS as permissões de ${selectedUser.email} pelas de ${u.email}?`,
                                                onConfirm: () => {
                                                    setUserPermissions(u.permissions || {});
                                                    setUserGroupIds(u.groupIds || (u.groupId ? [u.groupId] : []));
                                                    setUserAllowedCompanies(u.allowedCompanies || []);
                                                    setUserUiItems(u.uiItems || {});
                                                    setShowCopyModal(false);
                                                    setMessage(`✓ Perfil de ${u.email} copiado com sucesso!`);
                                                    setTimeout(() => setMessage(''), 4000);
                                                },
                                                confirmColor: "bg-purple-600",
                                                confirmText: "Confirmar Cópia"
                                            });
                                        }}
                                        className="w-full text-left p-4 hover:bg-slate-800 transition-all flex flex-col group"
                                    >
                                        <span className="font-bold text-slate-200 group-hover:text-purple-400 transition-colors">{u.email}</span>
                                        <span className="text-[9px] text-slate-500 uppercase font-mono mt-1">{u.uid}</span>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmationModal />
        </div>
    );
}